// scheduling/utils/managerJobData.js
// Shared manager-side job derivations, extracted from ManagerDashboard's memos +
// load-effect conflict scan. Computed ONCE in the Dashboard owner and passed to
// both OverviewView and JobsView (which are data-coupled) so nothing is computed
// twice or drifts. Gated in the owner so plain staff never pay for it.

import { SERVICE_TO_DEPT, computeSchedStatus, isLeadOf } from './schedConstants';
import { weekDayISOs } from './capacity';

function toISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
// Monday (ISO) of the week containing `iso`.
function weekMondayISO(iso) {
  const d = new Date(String(iso).slice(0, 10) + 'T12:00:00');
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * @param jobs  the (demo-augmented) firm-wide jobs list
 * @returns { enrichedJobs, myJobs, wipByJob, hrsByJob, conflicts }
 */
export function computeManagerJobData({ jobs, schedule, jobsWIP, staff, meStaffId, getDayCap, dayHoursByStaff }) {
  // hours-per-job (today-onward only — past hours are actuals/WIP)
  const todayISO = toISO(new Date());
  const hrsByJob = {};
  for (const e of (schedule || [])) {
    const eISO = String(e.EntryDate || '').slice(0, 10);
    if (eISO && eISO < todayISO) continue;
    const jid = e.JobId;
    if (!hrsByJob[jid]) hrsByJob[jid] = { total: 0, entries: [] };
    hrsByJob[jid].total += (e.Hours || 0);
    hrsByJob[jid].entries.push(e);
  }

  // WIP hours grouped by job
  const wipByJob = {};
  for (const w of (jobsWIP || [])) {
    if (!w.JobId) continue;
    if (!wipByJob[w.JobId]) wipByJob[w.JobId] = { total: 0, byStaff: [] };
    wipByJob[w.JobId].total += w.TotalHours || 0;
    wipByJob[w.JobId].byStaff.push({ staffId: w.StaffId, name: w.StaffName || 'Unknown', initials: w.StaffInitials || '??', hours: w.TotalHours || 0 });
  }
  for (const jid of Object.keys(wipByJob)) wipByJob[jid].byStaff.sort((a, b) => b.hours - a.hours);

  // enriched jobs (scheduled hrs today-onward + dept + sched status)
  const enrichedJobs = (jobs || []).map(j => {
    const budget = j.BudgetHours || 0;
    // Placeholder hours count as scheduled — the calendar space is allotted, only the
    // name is outstanding. Comes down on the job itself (schedData queryJobs
    // PlaceholderHours, today-onward) so no surface needs its own placeholder fetch.
    // Folded into scheduledHrs so the status chip, the Needs Attention alerts and the
    // over-budget maths all move together.
    const placeholder = j.PlaceholderHours || 0;
    const sched = Math.round(((hrsByJob[j.JobId]?.total || 0) + placeholder) * 100) / 100;
    const wip = wipByJob[j.JobId]?.total || 0;
    const dept = SERVICE_TO_DEPT[j.ServiceDesc] || null;
    const status = computeSchedStatus(budget, sched, wip);
    return { ...j, scheduledHrs: sched, placeholderHrs: placeholder, dept, schedStatus: status };
  });

  const myJobs = meStaffId ? enrichedJobs.filter(j => isLeadOf(j, meStaffId)) : enrichedJobs;

  // Scheduling conflicts — a staff-week where scheduled hours exceed capacity
  // (PTO/holiday/recurring-season aware via the shared getDayCap).
  const conflicts = [];
  try {
    const staffById = new Map((staff || []).map(s => [s.StaffId, s]));
    const seen = new Set();
    for (const e of (schedule || [])) {
      const iso = String(e.EntryDate).slice(0, 10);
      if (!iso) continue;
      const mon = weekMondayISO(iso);
      const key = `${e.StaffId}|${mon}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const dh = dayHoursByStaff[e.StaffId] || {};
      let sched = 0, cap = 0;
      for (const d of weekDayISOs(mon)) { sched += dh[d] || 0; cap += getDayCap(e.StaffId, d); }
      sched = Math.round(sched * 100) / 100;
      cap = Math.round(cap * 100) / 100;
      if (sched > cap) {
        const s = staffById.get(e.StaffId) || {};
        conflicts.push({
          StaffId: e.StaffId, StaffName: s.Name, Level: s.Level, DepartmentCode: s.DepartmentCode,
          WeekStartDate: mon, TotalScheduled: sched, CapacityHours: cap,
          OverageHours: Math.round((sched - cap) * 100) / 100,
        });
      }
    }
  } catch { /* ignore */ }

  return { enrichedJobs, myJobs, wipByJob, hrsByJob, conflicts };
}

/** Stat-bar counts for a source job list (My Jobs on overview/my, all jobs on all). */
export function computeJobStats(source) {
  return {
    total: source.length,
    full: source.filter(j => j.schedStatus === 'scheduled').length,
    needs: source.filter(j => j.schedStatus === 'unscheduled' || j.schedStatus === 'partial').length,
    overbudget: source.filter(j => j.schedStatus === 'overbudget' || j.schedStatus === 'overscheduled').length,
    nobudget: source.filter(j => j.schedStatus === 'nobudget').length,
  };
}
